import React, { useState, useEffect } from "react";
import { Table, Tag, Input, Button, Card, Tooltip, Spin } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
/* import { campusApi } from "../../services/api"; */
import { usuariosApi, rolesApi, campusApi } from "../../services/api";
import UsuarioFormModal from "./UsuarioFormModal";
import DeleteConfirmModal from "../DeleteConfirmModal";
import PasswordChangeModal from "./PasswordChangeModal";

const TablaUsuarios = () => {
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [campus, setCampus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [userForPasswordChange, setUserForPasswordChange] = useState(null);

  // Cargar datos al iniciar el componente
  useEffect(() => {
    Promise.all([fetchUsuarios(), fetchRoles(), fetchCampus()])
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error cargando datos iniciales:", error);
        toast.error("Error al cargar los datos", {
          position: "top-right",
          autoClose: 4000,
        });
        setLoading(false);
      });
  }, []);

  // Función para obtener usuarios
  const fetchUsuarios = async () => {
    try {
      const response = await usuariosApi.getAll();
      if (response.data.status) {
        const usuariosData = response.data.usuarios.map((usuario) => ({
          key: usuario.id.toString(),
          id: usuario.id,
          nombre: usuario.name,
          email: usuario.email,
          cedula: usuario.cedula,
          rol: usuario.rol.rol,
          rol_id: usuario.rol_id,
          campus: usuario.campus.campus,
          campus_id: usuario.campus_id,
          estado: usuario.estado,
        }));
        setUsuarios(usuariosData);
      }
    } catch (error) {
      console.error("Error obteniendo usuarios:", error);
      toast.error("Error al cargar los usuarios", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Función para obtener roles
  const fetchRoles = async () => {
    try {
      const response = await rolesApi.getAll();
      if (response.data.status) {
        setRoles(response.data.roles);
      }
    } catch (error) {
      console.error("Error obteniendo roles:", error);
      toast.error("Error al cargar los roles", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Función para obtener campus
  const fetchCampus = async () => {
    try {
      const response = await campusApi.getAll();
      if (response.data.status) {
        setCampus(response.data.campus);
      }
    } catch (error) {
      console.error("Error obteniendo campus:", error);
      toast.error("Error al cargar los campus", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90, marginRight: 8 }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Buscar
        </Button>
        <Button
          onClick={() => clearFilters()}
          size="small"
          style={{ width: 90 }}
        >
          Limpiar
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const columns = [
    {
      title: <div className="font-medium">Nombre</div>,
      dataIndex: "nombre",
      key: "nombre",
      ...getColumnSearchProps("nombre"),
      render: (text) => (
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-2">
            <UserOutlined />
          </div>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div className="font-medium">Email</div>,
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: <div className="font-medium">Cédula</div>,
      dataIndex: "cedula",
      key: "cedula",
      ...getColumnSearchProps("cedula"),
    },
    {
      title: <div className="font-medium">Campus</div>,
      dataIndex: "campus",
      key: "campus",
      filters: campus.map((c) => ({ text: c.campus, value: c.campus })),
      onFilter: (value, record) => record.campus === value,
    },
    {
      title: <div className="font-medium">Rol</div>,
      dataIndex: "rol",
      key: "rol",
      filters: roles.map((rol) => ({ text: rol.rol, value: rol.rol })),
      onFilter: (value, record) => record.rol === value,
      render: (rol) => {
        let color = rol === "Administrador" ? "blue" : "cyan";
        return (
          <Tag color={color} className="py-1 px-2">
            {rol}
          </Tag>
        );
      },
    },
    {
      title: <div className="font-medium">Estado</div>,
      dataIndex: "estado",
      key: "estado",
      filters: [
        { text: "Activo", value: "activo" },
        { text: "Inactivo", value: "inactivo" },
      ],
      onFilter: (value, record) => record.estado === value,
      render: (estado) => (
        <Tag
          color={estado === "activo" ? "green" : "red"}
          className="py-1 px-2"
        >
          {estado === "activo" ? "Activo" : "Inactivo"}
        </Tag>
      ),
    },
    {
      title: <div className="font-medium text-center">Acciones</div>,
      key: "acciones",
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <Tooltip title="Editar usuario">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              type="primary"
              size="middle"
              className="bg-blue-500 hover:bg-blue-700"
            />
          </Tooltip>
          <Tooltip title="Cambiar contraseña">
            <Button
              icon={<KeyOutlined />}
              onClick={() => handlePasswordChange(record)}
              size="middle"
              className="bg-gray-400 hover:bg-gray-500"
            />
          </Tooltip>
          <Tooltip title="Eliminar usuario">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              type="primary"
              danger
              size="middle"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleReset = () => {
    setSearchText("");
    fetchUsuarios();
  };

  const showModal = () => {
    setIsModalVisible(true);
    setEditingUser(null);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsModalVisible(true);
  };

  const handleDelete = (user) => {
    setUserToDelete(user);
    setDeleteModalVisible(true);
  };

  const handlePasswordChange = (user) => {
    setUserForPasswordChange(user);
    setPasswordModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await usuariosApi.delete(userToDelete.id);
      toast.success(`Usuario ${userToDelete.nombre} eliminado correctamente`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setDeleteModalVisible(false);
      setUserToDelete(null);
      fetchUsuarios(); // Recargar la lista de usuarios
    } catch (error) {
      console.error("Error eliminando usuario:", error);
      let errorMsg = "Error al eliminar el usuario";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMsg = error.response.data.message;
      }
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    setEditingUser(null);
    fetchUsuarios(); // Recargar la lista después de un cambio exitoso
  };

  const handlePasswordModalSuccess = () => {
    setPasswordModalVisible(false);
    setUserForPasswordChange(null);
   
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-[calc(100vh-100px)]">
        <Spin size="large" tip="Cargando usuarios..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card
        title={
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-gray-800">
              Administración de Usuarios
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showModal}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Nuevo Usuario
            </Button>
          </div>
        }
        className="shadow-md"
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <Input
              placeholder="Buscar usuario..."
              className="w-72"
              prefix={<SearchOutlined className="text-gray-400" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
            />
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            type="default"
            className="h-10"
          >
            Reiniciar filtros
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={usuarios.filter(
            (item) =>
              item.nombre.toLowerCase().includes(searchText.toLowerCase()) ||
              item.email.toLowerCase().includes(searchText.toLowerCase()) ||
              (item.cedula &&
                item.cedula.toLowerCase().includes(searchText.toLowerCase()))
          )}
          rowClassName={() =>
            "hover:bg-blue-50 transition-colors cursor-pointer"
          }
          scroll={{ y: "calc(100vh - 350px)" }}
          pagination={{
            total: usuarios.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} usuarios`,
          }}
          className="border border-gray-200 rounded-md"
        />

        {/* Modal para crear/editar usuarios */}
        <UsuarioFormModal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={handleModalSuccess}
          editingUser={editingUser}
          roles={roles}
          campus={campus}
        />

        {/* Modal de confirmación para eliminar usuario */}
        <DeleteConfirmModal
          visible={deleteModalVisible}
          onCancel={() => setDeleteModalVisible(false)}
          onConfirm={confirmDelete}
          title="Eliminar Usuario"
          itemDescription="al usuario"
          itemName={userToDelete ? userToDelete.nombre : ""}
        />

        {/* Modal para cambiar contraseña */}
        <PasswordChangeModal
          visible={passwordModalVisible}
          onCancel={() => setPasswordModalVisible(false)}
          onSuccess={handlePasswordModalSuccess}
          user={userForPasswordChange}
        />
      </Card>
    </div>
  );
};

export default TablaUsuarios;
